/**
 * Reading and changing who can see a shared artifact or skill (AI-734).
 *
 * A share is a `pi.resources` row of type `artifact` or `skill` — created on first share for an
 * artifact, already present for a skill. Its recipients are ordinary resource permissions, so the
 * grants below are the same `updatePermissions` mutation the account's permission screens use — only
 * the level is fixed, at READ.
 */

import { useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { UseMutationResult } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { rejectOnGraphqlErrors, rejectOnRequestLevelGraphqlErrors, type ResultWithApiMessages } from "gatherers/extract-graphql-error-message"

/** Every recipient in v1 gets READ. WRITE is deferred to the collaborative-editing work. */
export const SHARE_PERMISSION_LEVEL = "READ"
export type ShareableResourceType = "ARTIFACT" | "SKILL"
const NO_ACCESS = "NONE"
const OWNER = "OWNER"

export interface DirectoryEntry {
  kind: "member" | "team"
  id: string
  name: string
  /** Members only — the disambiguator when two colleagues share a display name. */
  email?: string
}

export interface ShareRecipient extends DirectoryEntry {
  /** OWNER is the sharer's own grant, which the dialog shows but never offers to remove. */
  isOwner: boolean
}

export interface ArtifactShare {
  resourceId: string
  name: string
  sourceArtifactId: string
}

interface GraphqlResult<T> extends ResultWithApiMessages { data: T }

const SHARED_ARTIFACTS_QUERY = `query {
  resources(type: ARTIFACT) {
    ... on ArtifactResourceMeta {
      id
      name
      filename
      contentType
      sourceArtifactId
      createdBy
      sharedAt
      permissionLevel
    }
  }
}`

function shareRecipientsQuery(resourceType: ShareableResourceType): string {
  return `query ($resourceId: IdOrShortIdOrAlias!) {
  resource(id: $resourceId, type: ${resourceType}) {
    id
    members { id name email permissionLevel }
    teams { id name permissionLevel }
  }
}`
}

const DIRECTORY_QUERY = `query {
  members { id name email }
  teams { id name }
}`

/**
 * One `updatePermissions` per recipient, aliased into a single mutation — the same shape the
 * resource-permissions screens send. The level is a variable; the ids are not, because a GraphQL
 * alias cannot be one.
 */
function permissionsMutation(recipients: DirectoryEntry[], resourceType: ShareableResourceType): string {
  const fields = recipients.map(({ kind, id }, index) => {
    const entity = kind === "team" ? `teamId: "${id}"` : `memberId: "${id}"`
    return `r${index}: updatePermissions(resourceType: ${resourceType}, resourceId: $resourceId, permissions: { ${entity}, permissionLevel: $permissionLevel }) { id }`
  })
  return `mutation ($resourceId: IdOrShortIdOrAlias!, $permissionLevel: PermissionLevel!) {\n  ${fields.join("\n  ")}\n}`
}

function useGraphql() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  // A GraphQL field error still resolves the saga, so without this a refused query would run the
  // success path. Reads have no partial result worth keeping, so any error rejects.
  return useCallback(<T>(query: string, variables: Record<string, unknown> = {}): Promise<GraphqlResult<T>> => (
    new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, query, variables, rejectOnGraphqlErrors<GraphqlResult<T>>(value => resolve(value), reject), reject))
    })
  ), [account, dispatch])
}

/**
 * The permissions mutation is one `updatePermissions` per recipient, aliased into a single request,
 * and each alias succeeds or fails on its own. Rejecting the whole call on any field error would
 * hide the grants that did land and skip the cache invalidation that shows them — so only a
 * request-level error rejects, and the caller reports which aliases failed.
 */
function usePermissionsGraphql() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(<T>(query: string, variables: Record<string, unknown> = {}): Promise<GraphqlResult<T>> => (
    new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, query, variables, rejectOnRequestLevelGraphqlErrors<GraphqlResult<T>>(value => resolve(value), reject), reject))
    })
  ), [account, dispatch])
}

/** The recipients an aliased permissions mutation refused, named by the alias index it used. */
function refusedRecipients(result: GraphqlResult<unknown>, recipients: DirectoryEntry[]): DirectoryEntry[] {
  const errors = result?.apiMessages?.errors ?? []
  const refusedIndexes = new Set(
    errors
      .map(error => /^r(\d+)$/.exec(String(error.path?.[0] ?? "")))
      .filter((match): match is RegExpExecArray => match != null)
      .map(match => Number(match[1])),
  )
  return recipients.filter((_, index) => refusedIndexes.has(index))
}

// Scoped by account: the QueryClient in src/root.jsx is one module-level instance and an account
// switch is client-side, so an unscoped key serves the previous account's people and shares on mount.
export const SHARE_QUERY_KEYS = {
  shares: (accountAlias: string) => ["kelly", accountAlias, "artifact-shares"] as const,
  recipients: (accountAlias: string, resourceId: string | null) => ["kelly", accountAlias, "share-recipients", resourceId] as const,
  directory: (accountAlias: string) => ["kelly", accountAlias, "share-directory"] as const,
}

export interface SharedArtifactRow {
  id: string
  name: string
  filename: string
  contentType: string
  sourceArtifactId: string
  /** The member who shared it — the exact "Yours" test, and the name under "Shared with you". */
  createdBy: string
  sharedAt: string
  permissionLevel: string
}

/**
 * The share this user made of this artifact, if there is one. Derived from the artifacts list rather
 * than from a lookup endpoint of its own: the list is what the Artifacts surface loads anyway, and
 * asking the share endpoint would create the share as a side effect of opening a dialog.
 */
export function useArtifactShare(sourceArtifactId: string | null, enabled: boolean) {
  const account = useAccountAlias()
  const graphql = useGraphql()

  const { data, isPending, isError } = useQuery({
    queryKey: SHARE_QUERY_KEYS.shares(account),
    queryFn: () => graphql<{ resources: SharedArtifactRow[] }>(SHARED_ARTIFACTS_QUERY),
    enabled,
    select: (result): SharedArtifactRow[] => result.data.resources ?? [],
  })

  const match = sourceArtifactId == null
    ? undefined
    : data?.find(row => row.sourceArtifactId === sourceArtifactId && row.permissionLevel === OWNER)

  const share: ArtifactShare | null = match == null
    ? null
    : { resourceId: match.id, name: match.name, sourceArtifactId: match.sourceArtifactId }

  return { share, isLoading: enabled && isPending, isError }
}

interface RecipientsResult {
  resource: {
    members: { id: string, name: string, email?: string, permissionLevel: string }[] | null
    teams: { id: string, name: string, permissionLevel: string }[] | null
  } | null
}

/** Who can see the share today. The sharer's own OWNER grant is included, flagged, never removable. */
export function useShareRecipients(resourceId: string | null, resourceType: ShareableResourceType) {
  const account = useAccountAlias()
  const graphql = useGraphql()

  const { data, isPending, isError } = useQuery({
    queryKey: SHARE_QUERY_KEYS.recipients(account, resourceId),
    queryFn: () => graphql<RecipientsResult>(shareRecipientsQuery(resourceType), { resourceId }),
    enabled: resourceId != null,
    select: (result): ShareRecipient[] => {
      const resource = result.data.resource
      if (resource == null) return []
      return [
        ...(resource.teams ?? []).map(team => ({ kind: "team" as const, id: team.id, name: team.name, isOwner: false })),
        ...(resource.members ?? []).map(member => ({
          kind: "member" as const,
          id: member.id,
          name: member.name,
          email: member.email,
          isOwner: member.permissionLevel === OWNER,
        })),
      ]
    },
  })

  return { recipients: data ?? [], isLoading: resourceId != null && isPending, isError }
}

interface DirectoryResult {
  members: { id: string, name: string, email?: string }[] | null
  teams: { id: string, name: string }[] | null
}

/**
 * Everyone in the account who could be given access. No team member counts: `Team.members` resolves
 * per team, so asking for them would turn one query into one per team for secondary text.
 */
export function useShareDirectory(enabled: boolean) {
  const account = useAccountAlias()
  const graphql = useGraphql()

  const { data, isPending, isError } = useQuery({
    queryKey: SHARE_QUERY_KEYS.directory(account),
    queryFn: () => graphql<DirectoryResult>(DIRECTORY_QUERY),
    enabled,
    select: (result): DirectoryEntry[] => ([
      ...(result.data.teams ?? []).map(team => ({ kind: "team" as const, id: team.id, name: team.name })),
      ...(result.data.members ?? []).map(member => ({ kind: "member" as const, id: member.id, name: member.name, email: member.email })),
    ]),
  })

  return { directory: data ?? [], isLoading: enabled && isPending, isError }
}

interface CreateShareArgs { threadId: string, artifactId: string }
interface CreateShareResponse { resourceId: string, name: string, filename: string, contentType: string }

/**
 * Promotes the artifact to a shared resource. Idempotent per (artifact, sharer) on the server, so a
 * repeat call returns the existing share rather than a second copy of the bytes.
 */
export function useCreateArtifactShare(): UseMutationResult<CreateShareResponse, Error, CreateShareArgs> {
  const account = useAccountAlias()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ threadId, artifactId }: CreateShareArgs) => new Promise<CreateShareResponse>((resolve, reject) => {
      dispatch(Creators.post(
        `/api/${encodeURIComponent(account)}/ai/chat/threads/${encodeURIComponent(threadId)}/artifacts/${encodeURIComponent(artifactId)}/share`,
        {},
        resolve,
        reject,
      ))
    }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: SHARE_QUERY_KEYS.shares(account) }) },
  })
}

interface GrantArgs { resourceId: string, recipients: DirectoryEntry[] }

export function useGrantShareAccess(resourceType: ShareableResourceType): UseMutationResult<DirectoryEntry[], Error, GrantArgs> {
  const account = useAccountAlias()
  const graphql = usePermissionsGraphql()
  const queryClient = useQueryClient()

  return useMutation({
    // Resolves with whoever was refused, so a partial grant still reports the ones that landed.
    mutationFn: async ({ resourceId, recipients }: GrantArgs) => {
      const result = await graphql(permissionsMutation(recipients, resourceType), { resourceId, permissionLevel: SHARE_PERMISSION_LEVEL })
      return refusedRecipients(result, recipients)
    },
    // Runs on a partial grant too — the recipients who did get access must appear in the list.
    onSuccess: (_refused, { resourceId }) => {
      void queryClient.invalidateQueries({ queryKey: SHARE_QUERY_KEYS.recipients(account, resourceId) })
    },
  })
}

interface RevokeArgs { resourceId: string, recipient: DirectoryEntry }

export function useRevokeShareAccess(resourceType: ShareableResourceType): UseMutationResult<unknown, Error, RevokeArgs> {
  const account = useAccountAlias()
  const graphql = usePermissionsGraphql()
  const queryClient = useQueryClient()

  return useMutation({
    // One alias, so a field-scoped error here means the single revoke failed — surface it.
    mutationFn: async ({ resourceId, recipient }: RevokeArgs) => {
      const result = await graphql(permissionsMutation([recipient], resourceType), { resourceId, permissionLevel: NO_ACCESS })
      if (refusedRecipients(result, [recipient]).length > 0) throw new Error("The access could not be removed.")
      return result
    },
    onSuccess: (_result, { resourceId }) => {
      void queryClient.invalidateQueries({ queryKey: SHARE_QUERY_KEYS.recipients(account, resourceId) })
    },
  })
}

/** The URL a recipient opens. AI-994 owns the route this points at. */
export function artifactShareUrl(accountAlias: string, resourceId: string): string {
  return `${window.location.origin}/${encodeURIComponent(accountAlias)}/kelly-ai/artifacts/${encodeURIComponent(resourceId)}`
}

/**
 * Every artifact this user can see — theirs and the ones shared with them. `withResourcePermissions`
 * has already filtered it server-side, so there is nothing to filter here for access; the pills split
 * it on `createdBy`.
 */
export function useSharedArtifacts(enabled: boolean) {
  const account = useAccountAlias()
  const graphql = useGraphql()

  const { data, isPending, isError } = useQuery({
    queryKey: SHARE_QUERY_KEYS.shares(account),
    queryFn: () => graphql<{ resources: SharedArtifactRow[] }>(SHARED_ARTIFACTS_QUERY),
    enabled,
    select: (result): SharedArtifactRow[] => result.data.resources ?? [],
  })

  return { artifacts: data ?? [], isLoading: enabled && isPending, isError }
}

/**
 * Account members, for turning a `createdBy` id into a name. Reuses the share dialog's directory query,
 * and reports its loading state: the names arrive on a second round trip, so a caller that renders rows
 * off the first would show a confident wrong answer for every colleague until this settles.
 */
export function useMemberNames(enabled: boolean): { names: Record<string, string>, isLoading: boolean, isError: boolean } {
  const { directory, isLoading, isError } = useShareDirectory(enabled)

  const names = useMemo(() => Object.fromEntries(
    directory.filter(entry => entry.kind === "member").map(entry => [entry.id, entry.name]),
  ), [directory])

  // isError travels with the names: an empty directory from a FAILED query is indistinguishable from
  // an account of one, and the column's em dash means something specific the caller must not claim.
  return { names, isLoading, isError }
}
